import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { API_ENDPOINTS, fetchFromApi } from '../config/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PastEventCard from '../components/PastEventCard';
import '../styles/MixesPage.css';

const ITEMS_PER_PAGE = 4;

const MixesPage = () => {
  const [allMixes, setAllMixes] = useState([]);
  const [displayedMixes, setDisplayedMixes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  // Fetch all mixes once
  useEffect(() => {
    const fetchMixes = async () => {
      try {
        setLoading(true);
        const data = await fetchFromApi(`${API_ENDPOINTS.pastEvents}?limit=100`, {
          method: 'GET',
          mode: 'cors'
        });

        if (!data || data.error) {
          console.error('Error fetching mixes:', data?.error);
          setError('Failed to load mixes');
          setAllMixes([]);
          return;
        }

        // Sort by date (newest first)
        const sortedMixes = data.sort((a, b) => new Date(b.event_start) - new Date(a.event_start));
        setAllMixes(sortedMixes);
        // Show first batch
        setDisplayedMixes(sortedMixes.slice(0, ITEMS_PER_PAGE));
        setHasMore(sortedMixes.length > ITEMS_PER_PAGE);
        setError(null);
      } catch (err) {
        console.error('Error fetching mixes:', err);
        setError('Failed to load mixes');
        setAllMixes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMixes();
  }, []);

  // Load more mixes
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    const currentCount = displayedMixes.length;
    const nextBatch = allMixes.slice(currentCount, currentCount + ITEMS_PER_PAGE);
    
    setTimeout(() => {
      setDisplayedMixes(prev => [...prev, ...nextBatch]);
      setHasMore(currentCount + nextBatch.length < allMixes.length);
      setLoadingMore(false);
    }, 300); // Small delay for smooth UX
  }, [allMixes, displayedMixes.length, hasMore, loadingMore]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, loadingMore, loadMore]);

  return (
    <>
      <Helmet>
        <title>Mixes | The Lost Loft</title>
        <meta name="description" content="Explore recorded DJ sets and mixes from The Lost Loft events. Chicago underground house and techno sessions available for streaming." />
        <link rel="canonical" href="https://thelostloft.com/mixes" />
        <meta property="og:title" content="Mixes | The Lost Loft" />
        <meta property="og:description" content="Explore recorded DJ sets and mixes from The Lost Loft events. Chicago underground house and techno sessions available for streaming." />
        <meta property="og:url" content="https://thelostloft.com/mixes" />
      </Helmet>
      <Navbar />
      <main className="mixes-page">
        <div className="mixes-container">
          <h1>Mixes</h1>
          {loading ? (
            <div className="loading">Loading mixes...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : displayedMixes.length === 0 ? (
            <div className="no-mixes-message">No mixes available at this time.</div>
          ) : (
            <>
              <div className="mixes-grid">
                {displayedMixes.map((mix, index) => (
                  <PastEventCard key={`mix-${mix.id || mix.event_id || index}`} event={mix} />
                ))}
              </div>
              
              {/* Load more trigger */}
              <div ref={loadMoreRef} className="load-more-trigger">
                {loadingMore && <div className="loading-more">Loading more...</div>}
                {!hasMore && displayedMixes.length > ITEMS_PER_PAGE && (
                  <div className="end-of-list">You've reached the end</div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default MixesPage;
